import angular from "angular";
import { ApiImportStatus, ApiCheckStatus, Arrays, UserDefinedRow, Imports, DataMapper, DataMapperSelector, WorkerCheck, WorkerImportRow, WorkerImport, ApiValidationErrorType, Translations} from "@salaxy/core";

import { DataImportControllerBase } from "./DataImportControllerBase";

import { JsonSchemaService } from "../../../services";


/**
 * Controller for worker imports.
 */
export class WorkerImportController extends DataImportControllerBase<WorkerImportRow, WorkerImport, never> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Imports", "JsonSchemaService", "$timeout"];

  /**
   * Boolean indicating if the worker validation is in progress.
   */
  public isValidating = false;

  private getTargetKey = (x: WorkerImport) => `${x.officialId}_${x.item.officialPersonId}`.toLowerCase();
  private getSourceKey = (x: WorkerImportRow) => `${x.companyOfficialId}_${x.personalId}`.toLowerCase();
  private getCheckTargetKey = (x: WorkerCheck) => `${x.officialId}_${x.id}`.toLowerCase();
  private getCheckSourceKey = (x: WorkerImportRow) => `${x.companyOfficialId}_${x.personalId}`.toLowerCase();

  private extraValidator = {
    validate: () => this.validateWorkers(),
    buttonLabel: Translations.get("SALAXY.NG1.WorkerImport.validateWorkersButton")
  };

  /**
   * Creates a new WorkerImportController
   * @ignore
   */
  constructor(
    private importsApi: Imports,
    jsonSchemaService: JsonSchemaService,
    $timeout: angular.ITimeoutService
  ) {
    super(jsonSchemaService, $timeout);
  }

  /**
   * Implement IController
   */
  public async $onInit(){
    super.$onInit();
  }
  /** Import data */
  public async import() {

    if (this.isImporting) {
      return;
    }

    await this.validate();
    if (!this.targetData) {
      return;
    }

    this.isImporting = true;
    this.isCanceled = false;

    for(const sourceRow of this.sourceData) {
      sourceRow.validation = null;
      sourceRow.importStatus = ApiImportStatus.Ignored;
    }
    this.table.setData(this.sourceData);
    await this.$timeout();

    this.importTotal = this.targetData.length;
    this.importCurrent = 0;

    const chunkSize = 10;
    const chunks: WorkerImport[][] = [];
    for (let i = 0; i < this.targetData.length; i += chunkSize) {
        chunks.push(this.targetData.slice(i, i + chunkSize));
    }

    let index = 0;
    for(const chunk of chunks) {
      try {
        if (this.isCanceled) {
          break;
        }
        chunk.splice(0, chunk.length, ... await this.importsApi.importWorkers(chunk));
      } catch (error) {
        for( const importObj of chunk) {
          importObj.result = importObj.result ?? {};
          importObj.result.status = ApiImportStatus.Error,
          importObj.validation = {
            isValid: false,
            errors: [{
              key: null,
              type: ApiValidationErrorType.General,
              code: "Server error",
              msg: error?.data?.messageHtml || JSON.stringify(error?.data, Object.getOwnPropertyNames(error?.data)),
            }],
          };
        }
      }
      for(const result of chunk) {
        const sourceRows = this.sourceData.filter((x) => this.getSourceKey(x) == this.getTargetKey(result));
        for(const sourceRow of sourceRows) {
          sourceRow.validation = {
            isValid: result.validation?.isValid,
            errors: result.validation?.errors
          };
          sourceRow.importStatus = result.result?.status;
        }
        index += sourceRows.length;
        this.importCurrent = index;

        this.table.setData(this.sourceData);
        await this.$timeout();
      }
    }
    this.isImporting = false;
    await this.$timeout();
  }

  /** Help */
  public async help() {
    const helpData = [
      [Translations.get("SALAXY.NG1.WorkerImport.help.title")],
      [""],
      [Translations.get("SALAXY.NG1.WorkerImport.help.overview.title")],
      [Translations.get("SALAXY.NG1.WorkerImport.help.overview.row1")],
      [Translations.get("SALAXY.NG1.WorkerImport.help.overview.row2")],
      [""],
      ...this.getDefaultHelpDataForColumns(),
    ];
    await this.download(helpData);
  }

  /** Returns extra validator */
  public getExtraValidators(): Array<{ validate: () => void, buttonLabel: string }> {
      return [this.extraValidator];
  }

  protected async getImportMapper() {

    const rowMap = {
      defaultRowType: "rowType",
      defaultRowKind: "data.kind",
      defaultSalary: "price",
    };

    const rowMapper = new DataMapper<WorkerImportRow, UserDefinedRow>("NG1/WorkerImporController", "Työntekijät");
    await rowMapper.setSchemaFromCache(this.jsonSchemaService.getCache(), "salaxy-secure/schemas/v02.json", "UserDefinedRow", "target");
    rowMapper.setPaths(rowMap);
    rowMapper.generateSchema("WorkerImportRow", "source");


    const salaryDefaultSelector = new DataMapperSelector<WorkerImportRow, WorkerImport>(null,"item.employment.work.salaryDefaults",
    (source) => {
      if (source.defaultRowType && source.defaultSalary > 0) {
        return rowMapper.doMap([source]);
      }
      return [];
    });

    const mapper = new DataMapper<WorkerImportRow, WorkerImport>("NG1/WorkerImportController", "Työntekijät");
    const map = {
      companyOfficialId: "officialId",
      personalId: "item.officialPersonId",
      sourceId: "item.employment.sourceId",
      firstName: "item.avatar.firstName",
      lastName: "item.avatar.lastName",
      email: "item.contact.email",
      telephone: "item.contact.telephone",
      ibanNumber: "item.ibanNumber",
      street: "item.contact.street",
      postalCode: "item.contact.postalCode",
      city: "item.contact.city",
      countryCode: "item.contact.countryCode",
      startDate: "item.employment.startDate",
      employmentType: "item.employment.type",
      pensionCalculation: "item.employment.pensionCalculation",
      description: "item.employment.work.description",
      occupationCode: "item.employment.work.occupationCode",
      taxcard: "item.employment.taxcard",
      defaultRowType: salaryDefaultSelector,
    };
    await mapper.setSchemaFromCache(this.jsonSchemaService.getCache(), "salaxy-secure/schemas/v02.json", "WorkerImport", "target");
    mapper.setPaths(map);
    mapper.generateSchema("WorkerImportRow", "source");

    mapper.source.schema.properties.occupationCode["format"] ="Occupation";
    for(const property in rowMapper.source.schema.properties) {
      mapper.source.schema.properties[property] = rowMapper.source.schema.properties[property];
    }
    return mapper;
  }

  /** Import data */
  private async validateWorkers() {

    if (this.isValidating) {
      return;
    }

    await this.validate();
    if (!this.targetData) {
      return;
    }

    this.isValidating = true;

    for(const sourceRow of this.sourceData) {
      sourceRow.validation = null;
      sourceRow.validation = {
        isValid: null, // null on purpose
        errors: [{
          type: ApiValidationErrorType.General,
          msg: Translations.get("SALAXY.UI_Terms.wait"),
        }],
      };
      sourceRow.importStatus = ApiImportStatus.Ignored;
    }
    this.table.setData(this.sourceData);
    await this.$timeout();

    const workerGroups = Arrays.groupBy(this.sourceData, this.getCheckSourceKey);
    const workerChecks: WorkerCheck[] = [];
    for(const workerGroupKey in workerGroups) {
      workerChecks.push( {
        officialId: workerGroups[workerGroupKey][0].companyOfficialId,
        id: workerGroups[workerGroupKey][0].personalId,
      });
    }

    const chunkSize = 10;
    const chunks: WorkerCheck[][] = [];
    for (let i = 0; i < workerChecks.length; i += chunkSize) {
        chunks.push(workerChecks.slice(i, i + chunkSize));
    }

    for(const chunk of chunks) {
      try {
        chunk.splice(0, chunk.length, ... await this.importsApi.checkWorkers(chunk));
      } catch (error) {
        for( const checkObj of chunk) {
          checkObj.result = checkObj.result ?? {};
          checkObj.result.status = ApiCheckStatus.Error,
          checkObj.validation = {
            isValid: false,
            errors: [{
              key: null,
              type: ApiValidationErrorType.General,
              code: "Server error",
              msg: error?.data?.messageHtml || JSON.stringify(error?.data, Object.getOwnPropertyNames(error?.data)),
            }],
          };
        }
      }
      for(const result of chunk) {
        const sourceRows = this.sourceData.filter((x) => this.getCheckSourceKey(x) == this.getCheckTargetKey(result));
        for(const sourceRow of sourceRows) {
          sourceRow.validation = {
            isValid: result.validation.isValid,
            errors: result.validation.isValid ? (
                    (result.result.item == null || result.result.item.length == 0) ? [ {msg: Translations.get("SALAXY.NG1.WorkerImport.messageMissingEmployment"), type: ApiValidationErrorType.General,}] :
                    (result.result.item.length == 1) ? [ {msg: Translations.get("SALAXY.NG1.WorkerImport.messageExistingEmployment"), type: ApiValidationErrorType.General,}] :
                    (result.result.item.length > 1) ? [ {msg: Translations.get("SALAXY.NG1.WorkerImport.messageSeveralEmployments"), type: ApiValidationErrorType.General,}] : result.validation.errors) :
                    result.validation.errors
          };
        }

        this.table.setData(this.sourceData);
        await this.$timeout();
      }
    }
    this.isValidating = false;
    await this.$timeout();
  }
}